import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { programsAPI } from 'api';
import { RejectWithMessage, TableDataResponse } from 'features/types';
import { Paging } from 'api/types';
import { GetMyProgramsTable, MyProgramTableView, GetMyProgramsTableFilters } from 'features/programs/types';
import { getErrorMessage } from 'utils/error';
import { RootState } from 'app/root-reducer';

export const getAdminMemberGroupsTable = createAsyncThunk<TableDataResponse<MyProgramTableView>, void, RejectWithMessage>(
  'admin-member-groups/get-table',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      const requestData = state.adminMemberGroups.requestData;
      return await programsAPI.getAdminMemberTable(requestData);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialRequestData: GetMyProgramsTable = {
  filters: {},
  paging: {
    page: 1,
    pageSize: 20,
  },
};

type AdminMemberGroupsState = {
  requestData: GetMyProgramsTable;
  tableDataIsLoading: boolean;
  tableData: TableDataResponse<MyProgramTableView>;
};

const initialState: AdminMemberGroupsState = {
  requestData: initialRequestData,
  tableDataIsLoading: false,
  tableData: {
    rows: [],
    totalCount: 0,
    noResult: false,
  },
};

const adminMemberGroupsSlice = createSlice({
  name: 'adminMemberGroups',
  initialState,
  reducers: {
    resetRequestData: (state) => {
      state.requestData = { ...initialRequestData };
    },
    setPage: (state, action: PayloadAction<Paging>) => {
      state.requestData.paging.page = action.payload.page;
      state.requestData.paging.pageSize = action.payload.pageSize;
    },
    setFilters: (state, action: PayloadAction<Partial<GetMyProgramsTableFilters>>) => {
      state.requestData.filters = { ...action.payload };
      state.requestData.paging = initialRequestData.paging;
    },
    resetTableData: (state) => {
      state.tableData = initialState.tableData;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAdminMemberGroupsTable.pending, (state) => {
      state.tableDataIsLoading = true;
    });
    builder.addCase(getAdminMemberGroupsTable.fulfilled, (state, action: PayloadAction<TableDataResponse<MyProgramTableView>>) => {
      state.tableDataIsLoading = false;
      state.tableData = action.payload;
      state.tableData.noResult = state.tableData.rows.length === 0;
    });
    builder.addCase(getAdminMemberGroupsTable.rejected, (state) => {
      state.tableDataIsLoading = false;
    });
  },
});

export const { resetRequestData, setPage, setFilters, resetTableData } = adminMemberGroupsSlice.actions;
export const adminMemberGroupsReducer = adminMemberGroupsSlice.reducer;
